import React, { useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import {
    MenuItem,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import emptyCompanyAddress from 'components/routes/Pages/Refund/constants/emptyCompanyAddress';
import RefundForm from 'components/routes/Pages/Refund/models/RefundForm';
import TextFieldControlled from 'components/shared/TextFieldControlled';

import useAddressesSWR from 'hooks/addresses/useAddresses';
import useCountriesSWR from 'hooks/useCountries';

import DateTimePickerController from 'components/shared/DateTimePickerController';

import { useTranslation } from 'react-i18next';

import { cloneDeep } from 'lodash';

interface CourierServiceProps {
    formBase: 'transit' | 'courier_service';
}

const CourierService: React.FC<CourierServiceProps> = (props) => {
    const { formBase } = props;

    const { t: tBase } = useTranslation();

    const { t } = useTranslation('refund', {
        keyPrefix: 'SHIPPING_DETAILS_STEP',
    });

    const theme = useTheme();

    const small = useMediaQuery(theme.breakpoints.down('md'));

    const form = useFormContext<RefundForm>();

    const [startTime, endTime] = form.watch([
        `${formBase}.start_time`,
        `${formBase}.end_time`,
    ]);

    const { countries, isLoading: loadingCountries } = useCountriesSWR();

    const { addresses, isLoading: loadingAddresses } = useAddressesSWR();

    const extendedAddresses = useMemo(() => {
        if (!addresses) return [emptyCompanyAddress];
        return [emptyCompanyAddress, ...addresses];
    }, [addresses]);

    const deliveryAddressId = form.watch(`${formBase}.delivery_address_id`);

    useEffect((): void => {
        const address = extendedAddresses?.find(
            (item) => item.id === deliveryAddressId
        );

        if (!address) return;

        form.setValue(
            `${formBase}.delivery_address`,
            cloneDeep(address.shippingAddress),
            {
                shouldValidate: address.id !== emptyCompanyAddress.id,
                shouldDirty: address.id !== emptyCompanyAddress.id,
            }
        );
    }, [deliveryAddressId, extendedAddresses]);

    if (loadingAddresses || loadingCountries) return null;

    return (
        <Stack gap={2}>
            <TextFieldControlled
                control={form.control}
                name={`${formBase}.delivery_address_id`}
                label={t('ADDRESSES')}
                select
            >
                {extendedAddresses?.map((item, key) => (
                    <MenuItem key={key} value={item.id}>
                        {item.name}
                    </MenuItem>
                ))}
            </TextFieldControlled>

            <Stack gap={2}>
                <Typography variant="caption">
                    {t('DELIVERY_ADDRESS')}
                </Typography>

                <Stack flexDirection={small ? 'column' : 'row'} gap={2}>
                    <TextFieldControlled
                        fullWidth
                        control={form.control}
                        name={`${formBase}.delivery_address.country`}
                        label={t('COUNTRY')}
                        select
                        disabled
                    >
                        {countries?.map((item, key) => (
                            <MenuItem key={key} value={item}>
                                {tBase(`COUNTRIES.${item}`, {
                                    ns: 'countries',
                                })}
                            </MenuItem>
                        ))}
                    </TextFieldControlled>

                    <TextFieldControlled
                        control={form.control}
                        name={`${formBase}.delivery_address.postcode`}
                        label={t('POSTCODE')}
                        type="number"
                    />
                </Stack>

                <Stack
                    width={small ? '100%' : '50%'}
                    paddingRight={small ? null : 1}
                >
                    <TextFieldControlled
                        control={form.control}
                        name={`${formBase}.delivery_address.city`}
                        label={t('CITY')}
                    />
                </Stack>

                <Stack flexDirection={small ? 'column' : 'row'} gap={2}>
                    <TextFieldControlled
                        control={form.control}
                        name={`${formBase}.delivery_address.address1`}
                        label={t('ADDRESS_1')}
                        fullWidth
                    />

                    <TextFieldControlled
                        control={form.control}
                        name={`${formBase}.delivery_address.address2`}
                        label={t('ADDRESS_2')}
                        fullWidth
                    />
                </Stack>

                <Stack
                    width={small ? '100%' : '50%'}
                    paddingRight={small ? null : 1}
                >
                    <TextFieldControlled
                        control={form.control}
                        name={`${formBase}.delivery_address.phone`}
                        label={t('PHONE')}
                    />
                </Stack>

                <Typography variant="caption">
                    {t('REQUESTED_PACKAGE_PICKUP')}
                </Typography>

                <Stack flexDirection={small ? 'column' : 'row'} gap={2}>
                    <DateTimePickerController
                        control={form.control}
                        name={`${formBase}.start_time`}
                        label={t('START_TIME')}
                        maxDate={endTime.subtract(1, 'minute')}
                    />

                    <DateTimePickerController
                        control={form.control}
                        name={`${formBase}.end_time`}
                        label={t('END_TIME')}
                        minDate={startTime.add(1, 'minute')}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default CourierService;
